import React, { useState, useEffect } from 'react';
import {
  Palette,
  Check,
  Loader2,
  Sparkles,
  Layout as LayoutIcon,
  ExternalLink,
  Save,
  CheckCircle,
  AlertTriangle,
  Zap,
  Moon,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { THEMES, DEFAULT_THEME } from '../../themes/registry';
import { useTheme } from '../../themes/ThemeProvider';

const FEATURES = {
  theme1: [
    'Current production design',
    'Familiar, battle-tested layout',
    'All existing components & controls',
    'Zero behavioural changes',
  ],
  theme2: [
    'Premium agency UI/UX',
    'Parallax & scroll-triggered motion',
    'Glassmorphism + micro-interactions',
    'Built-in Light / Dark mode',
    'Magnetic buttons & tilt cards',
    'Optimized, reduced-motion aware',
  ],
};

const ThemeManagement = () => {
  const { applyTheme } = useTheme();
  const [selected, setSelected] = useState(DEFAULT_THEME);
  const [saved, setSaved] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(''); // 'success' | 'error' | ''
  const [message, setMessage] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await adminAPI.getSettings();
        const value = res?.data?.data?.settings?.active_theme?.value;
        const current = value || DEFAULT_THEME;
        setSelected(current);
        setSaved(current);
      } catch (e) {
        setSelected(DEFAULT_THEME);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setStatus('');
      setMessage('');
      await adminAPI.updateSettings({ active_theme: selected });
      setSaved(selected);
      // Instant switch: persist locally + broadcast to any open site tabs.
      applyTheme(selected, { persistLocal: true });
      setStatus('success');
      setMessage(`Theme switched to "${THEMES.find((t) => t.id === selected)?.name}". The website now uses this theme globally.`);
    } catch (e) {
      setStatus('error');
      setMessage('Failed to update theme. Please try again.');
    } finally {
      setSaving(false);
      setTimeout(() => {
        setStatus('');
        setMessage('');
      }, 6000);
    }
  };

  const dirty = selected !== saved;

  return (
    <div className="p-4 sm:p-6 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-lg">
            <Palette className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Theme Manager</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Choose the design that powers the entire frontend website. Backend, data and admin controls stay the same.
            </p>
          </div>
        </div>
      </div>

      {/* Status banner */}
      {status && (
        <div
          className={`mb-6 rounded-xl px-4 py-3 flex items-center gap-3 text-sm ${
            status === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}
        >
          {status === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {THEMES.map((theme) => {
              const isSel = selected === theme.id;
              const isLive = saved === theme.id;
              const isT2 = theme.id === 'theme2';
              return (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => setSelected(theme.id)}
                  className={`relative text-left rounded-2xl border-2 transition-all duration-300 overflow-hidden group ${
                    isSel
                      ? 'border-indigo-500 ring-4 ring-indigo-500/15 shadow-xl'
                      : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                  } bg-white dark:bg-gray-800`}
                >
                  {/* Preview banner */}
                  <div
                    className="h-40 relative overflow-hidden"
                    style={{
                      background: isT2
                        ? 'linear-gradient(135deg,#6366f1 0%,#8b5cf6 45%,#06b6d4 100%)'
                        : 'linear-gradient(135deg,#3b82f6 0%,#2563eb 100%)',
                    }}
                  >
                    <div className="absolute inset-0 opacity-25"
                      style={{
                        backgroundImage:
                          'linear-gradient(to right, rgba(255,255,255,.3) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,.3) 1px, transparent 1px)',
                        backgroundSize: '26px 26px',
                      }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center text-white">
                      {isT2 ? <Sparkles className="w-12 h-12 drop-shadow-lg" /> : <LayoutIcon className="w-12 h-12 drop-shadow-lg" />}
                    </div>
                    {isLive && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 text-gray-900 text-[11px] font-bold flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> LIVE
                      </span>
                    )}
                    {isSel && (
                      <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white text-indigo-600 flex items-center justify-center shadow-lg">
                        <Check className="w-5 h-5" />
                      </span>
                    )}
                  </div>

                  {/* Body */}
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {theme.name} <span className="text-gray-400 font-normal">· {theme.label}</span>
                      </h3>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{theme.description}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {theme.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <ul className="space-y-1.5">
                      {FEATURES[theme.id].map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                          <Check className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
            <button
              onClick={handleSave}
              disabled={saving || !dirty}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all ${
                dirty && !saving
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:shadow-lg hover:-translate-y-0.5'
                  : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
              }`}
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {saving ? 'Applying…' : dirty ? 'Apply Theme' : 'Theme Applied'}
            </button>

            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
            >
              <ExternalLink className="w-5 h-5" /> Preview Website
            </a>

            {dirty && (
              <span className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                <Zap className="w-4 h-4" /> Unsaved selection
              </span>
            )}
          </div>

          {/* Info note */}
          <div className="mt-8 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 p-4 text-sm text-blue-800 dark:text-blue-300 flex items-start gap-3">
            <Moon className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <strong>How it works:</strong> The selected theme is stored in site settings and loaded globally for every
              visitor. All content (services, products, pages, banners, testimonials, blog, menus) is shared between themes
              and stays fully editable from the admin panel. Theme 2 additionally provides a built-in Light/Dark toggle on the
              website.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeManagement;
